import { http } from '@beelday/common'
import { RoomAddress } from 'common/types'
import { SimpleGroupingTask } from './simple-grouping-redux'
import { useMemo } from 'react'
import { v4 as uuidv4 } from 'uuid'

export type SimpleGroupingApi = {
	start: (address: RoomAddress, task?: SimpleGroupingTask) => Promise<void>
	finish: (address: RoomAddress) => Promise<void>
}

export const createSimpleGroupingApi = (
	serverUrl: string
): SimpleGroupingApi => ({
	start: (address: RoomAddress, task?: SimpleGroupingTask) =>
		fetch(
			`${serverUrl}/api/v0/workflows/${address.workflowId}/interactionschemes/${address.interactionSchemeId}/rooms/${address.roomId}/features/simplegrouping`,
			{
				method: 'PUT',
				headers: {
					'Content-Type': 'application/json',
					'PB-Correlation-Id': uuidv4().replaceAll('-', '').slice(0, 16),
				},
				/**
				 * The backend's `SimpleGroupingApi.begin` takes a required
				 * `@RequestBody BeginRequest(name, description)`; sending nothing
				 * was a 400 on every call, which is why the trainer's restart
				 * never worked.
				 *
				 * The task has to be echoed back. `StartSimpleGrouping` overwrites
				 * `SimpleGrouping`'s `taskName` and `taskDescription` with whatever
				 * this body carries, so restarting group work with empty strings
				 * would blank the task the script had set - on screen, in front of
				 * the room. `useSimpleGrouping` binds the task last reported by
				 * `SIMPLE_GROUPING_STATE_CHANGED` so the caller cannot forget.
				 */
				body: JSON.stringify({
					name: task?.name ?? '',
					description: task?.description ?? '',
				}),
			}
		).then(http.checkStatus),
	finish: (address: RoomAddress) =>
		fetch(
			`${serverUrl}/api/v0/workflows/${address.workflowId}/interactionschemes/${address.interactionSchemeId}/rooms/${address.roomId}/features/simplegrouping`,
			{
				method: 'DELETE',
				headers: {
					'PB-Correlation-Id': uuidv4().replaceAll('-', '').slice(0, 16),
				},
			}
		).then(http.checkStatus),
})

export const useSimpleGroupingApi = (serverUrl: string): SimpleGroupingApi => {
	return useMemo(() => createSimpleGroupingApi(serverUrl), [serverUrl])
}
